import { useEffect, useRef, useState } from 'react';
import { ApiError, api, type RecordingSettingsView, type RoleView } from '../../api/client';
import { Button, Card, Chip, Field, LoadError, Loading, Notice } from '../../components/ui';
import { useData } from '../../lib/useData';

/** Customer-facing names for where Stitch keeps recordings. */
export const STITCH_STORAGE = 'Stitch Block Storage';
export const STITCH_DATABASE = 'Stitch database (testing)';

interface BucketForm {
  endpoint: string;
  region: string;
  bucket: string;
  prefix: string;
  accessKeyId: string;
  secret: string;
}
const EMPTY_BUCKET: BucketForm = { endpoint: '', region: '', bucket: '', prefix: 'stitch-recordings/', accessKeyId: '', secret: '' };

/** Session recording: on or off, who is recorded, how long recordings are kept, and the company's own storage. */
export function RecordingSettingsCard({ domain, companyName }: { domain: string; companyName: string }) {
  // Stitch's own settings, not the phone system's: never reloaded under someone filling the form in.
  const data = useData(() => Promise.all([api.recordingSettings(domain), api.access(domain)]), [domain], { live: false });
  const [enabled, setEnabled] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [days, setDays] = useState('30');
  const [own, setOwn] = useState(false);
  const [bucket, setBucket] = useState<BucketForm>(EMPTY_BUCKET);
  const [busy, setBusy] = useState<'' | 'save' | 'test'>('');
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');

  const s = data.data?.[0];
  const customRoles: RoleView[] = data.data?.[1].roles ?? [];
  const reset = (v: RecordingSettingsView) => {
    setEnabled(v.enabled);
    setRoles(v.roles);
    setDays(String(v.retentionDays));
    setOwn(Boolean(v.export));
    setBucket(v.export ? { ...v.export, secret: '' } : EMPTY_BUCKET);
  };
  // Filled in once per company; after that only by saving (a reload never wipes what's being typed).
  const filled = useRef<string | null>(null);
  useEffect(() => {
    if (s && filled.current !== domain) {
      reset(s);
      filled.current = domain;
    }
  }, [s, domain]);

  if (data.error)
    return (
      <Card title="Session recording">
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  if (!s)
    return (
      <Card title="Session recording">
        <Loading label="Loading session recording" />
      </Card>
    );

  const n = Number(days);
  const daysError = !/^\d+$/.test(days) || n < 1 || n > s.maxDays ? `Between 1 and ${s.maxDays} days (one year).` : '';
  const toggleRole = (r: string) => setRoles((list) => (list.includes(r) ? list.filter((x) => x !== r) : [...list, r]));
  const setB = (k: keyof BucketForm) => (e: { target: { value: string } }) => setBucket((b) => ({ ...b, [k]: e.target.value }));
  const where = s.storage === 'stitch' ? STITCH_STORAGE : STITCH_DATABASE;

  async function save() {
    setBusy('save');
    setError('');
    setSaved('');
    try {
      const next = await api.saveRecordingSettings(domain, {
        enabled,
        roles,
        retentionDays: n,
        export: own
          ? {
              endpoint: bucket.endpoint.trim(),
              region: bucket.region.trim(),
              bucket: bucket.bucket.trim(),
              prefix: bucket.prefix.trim(),
              accessKeyId: bucket.accessKeyId.trim(),
              ...(bucket.secret ? { secret: bucket.secret } : {})
            }
          : null
      });
      reset(next);
      data.reload();
      setSaved(
        next.enabled
          ? `Saved. People in the roles you chose are told the next time they sign in, and recorded from then on. Recordings are kept ${next.retentionDays} ${next.retentionDays === 1 ? 'day' : 'days'}${next.export ? `, and copied to ${next.export.bucket}` : ''}.`
          : 'Saved. Nobody is recorded. Recordings already made are kept until their time is up, or until you delete them under Activity.'
      );
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy('');
    }
  }

  async function test() {
    setBusy('test');
    setError('');
    setSaved('');
    try {
      await api.testRecordingBucket(domain);
      setSaved(`Stitch put a small test file (stitch-test.txt) in ${s?.export?.bucket ?? 'your bucket'}. Your storage works.`);
    } catch (err) {
      setError(err instanceof Error ? `Stitch couldn’t write to your storage: ${err.message}` : 'Stitch couldn’t write to your storage.');
    } finally {
      setBusy('');
    }
  }

  const changedBucket = own !== Boolean(s.export) || (own && (bucket.secret !== '' || (['endpoint', 'region', 'bucket', 'prefix', 'accessKeyId'] as const).some((k) => bucket[k] !== (s.export?.[k] ?? EMPTY_BUCKET[k]))));
  const changed = enabled !== s.enabled || n !== s.retentionDays || roles.slice().sort().join() !== s.roles.slice().sort().join() || changedBucket;

  return (
    <Card title="Session recording" action={<Chip small tone={s.enabled ? 'ok' : 'neutral'}>{s.enabled ? 'On' : 'Off'}</Chip>}>
      <div className="card-body settings-section">
        <p className="muted settings-lede">
          Record what people do in this portal, so you can see how something was done. People are told when they sign in, and see a note on screen while they’re
          recorded. What they type is hidden and passwords are never recorded. Stitch support isn’t recorded, and neither is live help. Who watched a recording shows
          under Activity.
        </p>

        <label className="check-row">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="cell-stack">
            <strong>Record portal sessions at {companyName}</strong>
            <small className="muted">For the people in the roles below.</small>
          </span>
        </label>

        <fieldset className="plain-fieldset rec-roles" disabled={!enabled}>
          <legend>Who is recorded</legend>
          <label className="check-row">
            <input type="checkbox" checked={roles.includes('admin')} onChange={() => toggleRole('admin')} />
            <span className="cell-stack">
              <strong>Admins</strong>
              <small className="muted">Everyone who manages the phone system here.</small>
            </span>
          </label>
          <label className="check-row">
            <input type="checkbox" checked={roles.includes('user')} onChange={() => toggleRole('user')} />
            <span className="cell-stack">
              <strong>Users</strong>
              <small className="muted">People who only see their own phone.</small>
            </span>
          </label>
          {customRoles.map((r) => (
            <label key={r.id} className="check-row">
              <input type="checkbox" checked={roles.includes(r.id)} onChange={() => toggleRole(r.id)} />
              <span className="cell-stack">
                <strong>{r.name}</strong>
                <small className="muted">Your custom role.</small>
              </span>
            </label>
          ))}
          {enabled && roles.length === 0 && <Notice tone="warn">Choose at least one role, or nobody is recorded.</Notice>}
        </fieldset>

        <div className="rec-days">
          <Field
            label="Keep recordings for (days)"
            type="number"
            inputMode="numeric"
            min={1}
            max={s.maxDays}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            {...(daysError ? { error: daysError } : {})}
            hint={`Days, up to ${s.maxDays} (one year). After that each recording is deleted from ${where}.`}
          />
        </div>

        <div className="rec-where">
          <span className="muted">Where recordings are kept</span>
          <span className="rec-where-chips">
            <StorageChip place={s.storage} />
            {s.export && <StorageChip place="theirs" bucket={s.export.bucket} />}
          </span>
        </div>
        {s.storage === 'database' && (
          <Notice tone="warn">
            Stitch Block Storage isn’t set up on this server yet, so recordings are kept in Stitch’s database for testing. Ask Stitch support before recording many people.
          </Notice>
        )}

        <div className="rec-own">
          <label className="check-row">
            <input type="checkbox" checked={own} disabled={!s.canExport} onChange={(e) => setOwn(e.target.checked)} />
            <span className="cell-stack">
              <strong>Also copy recordings to our own storage</strong>
              <small className="muted">
                Any S3-compatible storage (Amazon S3, Backblaze, Wasabi, and others). Each recording is copied a few minutes after it ends, and stays there as long as you
                keep it. The copy in {where} is still deleted after the days above.
              </small>
            </span>
          </label>
          {!s.canExport && <Notice tone="neutral">Your own storage can be added once Stitch support switches it on for this portal.</Notice>}
          {own && s.canExport && (
            <div className="form-grid rec-bucket">
              <Field label="Storage address" placeholder="https://s3.us-east-1.amazonaws.com" value={bucket.endpoint} onChange={setB('endpoint')} autoComplete="off" />
              <Field label="Region" placeholder="us-east-1" value={bucket.region} onChange={setB('region')} autoComplete="off" />
              <Field label="Bucket" placeholder="acme-recordings" value={bucket.bucket} onChange={setB('bucket')} autoComplete="off" />
              <Field label="Folder in the bucket" placeholder="stitch-recordings/" value={bucket.prefix} onChange={setB('prefix')} autoComplete="off" hint="Optional." />
              <Field label="Access key ID" value={bucket.accessKeyId} onChange={setB('accessKeyId')} autoComplete="off" data-private="" />
              <Field
                label="Secret access key"
                type="password"
                value={bucket.secret}
                onChange={setB('secret')}
                autoComplete="new-password"
                data-private=""
                placeholder={s.export?.hasSecret ? 'Saved. Leave blank to keep it.' : ''}
                hint="Kept encrypted. Nobody can see it again, including Stitch support."
              />
            </div>
          )}
          {own && s.canExport && <p className="muted small">Give this key permission to add files (PutObject) and read them (GetObject) in that bucket, and nothing more.</p>}
        </div>

        {saved && <Notice tone="ok">{saved}</Notice>}
        {error && <Notice tone="crit">{error}</Notice>}
        <div className="settings-actions">
          <Button variant="primary" disabled={Boolean(busy) || !changed || Boolean(daysError)} onClick={() => void save()}>
            {busy === 'save' ? 'Saving…' : 'Save session recording'}
          </Button>
          {s.export && !changedBucket && (
            <Button variant="secondary" disabled={Boolean(busy)} onClick={() => void test()}>
              {busy === 'test' ? 'Testing…' : 'Test our storage'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Where one recording is: Stitch's storage or the company's own. */
export function StorageChip({ place, bucket }: { place: 'stitch' | 'database' | 'theirs'; bucket?: string }) {
  if (place === 'theirs')
    return (
      <span className="storage-chip theirs" title="Your company’s own storage">
        Your storage{bucket ? `: ${bucket}` : ''}
      </span>
    );
  return <span className={`storage-chip ${place}`}>{place === 'stitch' ? STITCH_STORAGE : STITCH_DATABASE}</span>;
}
